import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CoreLibNotificationsPopupModalComponent } from 'app/core-lib/dialogs/notifications-popup-modal/notifications-popup-modal.component';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { ChartConfiguration, ChartData } from 'chart.js';
import { UtilService } from 'app/shared/util.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'mh-chart-value-widget',
    templateUrl: './chart-value-widget.component.html',
    styleUrls: ['./chart-value-widget.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'tw-flex tw-h-full tw-flex-col' },
})
export class ChartValueWidgetComponent implements OnInit {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() description = '';
  @Input() value = '';
  @Input() percentage = false;
  @Input() changePercentage: number | null = null;
  @Input() increase = true;
  @Input() chartData: ChartData = { datasets: [] };
  @Input() chartOptions: ChartConfiguration['options'] = {};
  @Input() loading = false;
  @Input() error = false;
  @Input() route = '';
  @Input() enabled = false;
  @Input() product: string;
  @Input() gaKey: string;
  @Input() demoRoute = '';
  @Input() showChart = true;
  @Input() showChange = true;

  public chartPlugins = [];
  public productsInfo: Record<string, { video: string; title: string }> = {
    onsite: {
      video: 'LCO0E0qz3M4',
      title: 'interchain-dashboard.widget_disabled.onsite_title',
    },
    followup: {
      video: 'ZHpYuXoIjfc',
      title: 'interchain-dashboard.widget_disabled.followup_title',
    },
    online: {
      video: '2bdg0rxgWWo',
      title: 'interchain-dashboard.widget_disabled.online_title',
    },
    cases: {
      video: 'h9IKjExiYgk',
      title: 'interchain-dashboard.widget_disabled.cases_title',
    },
  };

  private router = inject(Router);
  private modalService = inject(ModalService);
  private utilService = inject(UtilService);
  private translateService = inject(TranslateService);

  goToLink() {
    this.utilService.ga('corporative-dashboard', this.gaKey);
    const route = this.route.split('/').slice(0, -1).join('/');
    this.utilService.subjectExpandAll.next(route);
    this.router.navigate([this.route]);
  }

  ngOnInit() {
    this.chartOptions = {
      ...this.chartOptions,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { 
          display: false, 
          grid: { display: false } 
        },
        y: { 
          display: false, 
          grid: { display: false } 
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: {
          display: false,
        },
      },
      elements: { line: { tension: 0.4 } },
    };
  }

  seeDemo(product: string) {
    this.utilService.ga('corporative-dashboard', this.gaKey);
    if (this.demoRoute) {
      this.router.navigate([this.demoRoute]);
      return;
    }

    const classes = ['lg', 'overlay-panel', 'fit-height-content', '!tw-p-0'];
    const videoInfo = [
      {
        has_video: true,
        translates: [
          {
            title: this.translateService.instant(this.productsInfo[product].title),
            negative_button: this.translateService.instant('commons.close'),
            link: `https://www.youtube.com/watch?v=${this.productsInfo[product].video}`,
            banner: this.productsInfo[product].video,
          },
        ],
      },
    ];

    const config = {
      data: {
        noCloseOnBackdropClick: false,
        timer: false,
        simpleClose: true,
        simplePopup: true,
        news: videoInfo,
      },
    };
    this.modalService.open(CoreLibNotificationsPopupModalComponent, config, classes);
  }
}
